import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Globe,
  CheckCircle,
  XCircle,
  MapPin,
  Calendar,
  Plus,
  X,
  GitBranch,
  Search
} from 'lucide-react'
import merchantService, {
  Organization,
  UpdateOrganizationDto,
  CreateOrganizationRegionDto,
  OrganizationRegion
} from '@/services/merchantService'
import GlobalRegionSelector from './GlobalRegionSelector'
import { configService } from '@/services/configService'

const OrganizationDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [regions, setRegions] = useState<OrganizationRegion[]>([])
  const [loadingRegions, setLoadingRegions] = useState(false)
  const [showAddRegionModal, setShowAddRegionModal] = useState(false)
  const [addingRegion, setAddingRegion] = useState(false)
  const [addRegionMode, setAddRegionMode] = useState<'select' | 'create'>('select')
  const [selectedGlobalRegionId, setSelectedGlobalRegionId] = useState<string>('')
  const [newRegionData, setNewRegionData] = useState<CreateOrganizationRegionDto>({
    name: '',
    code: '',
    description: '',
    is_active: true
  })
  const [newGlobalRegionData, setNewGlobalRegionData] = useState<any>({
    name: '',
    code: '',
    country_id: '',
    type_id: '',
    tax_rate: 0,
    enabled: true
  })
  const [countries, setCountries] = useState<any[]>([])
  const [regionTypes, setRegionTypes] = useState<any[]>([])

  useEffect(() => {
    if (id) {
      loadOrganization()
      loadOrganizationRegions()
    }
  }, [id])

  // Load countries and region types for creating new global regions
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        // Load countries
        const countriesResponse = await configService.countries.getAll({ enabled: true })
        setCountries(countriesResponse.data || [])
        
        // Load region types from config service
        try {
          const regionTypesData = await configService.regionTypes.getAll()
          setRegionTypes(regionTypesData)
        } catch (regionTypesError) {
          console.warn('Failed to load region types from config service, using fallback', regionTypesError)
          // Fallback to hardcoded types if API fails
          setRegionTypes(getFallbackRegionTypes())
        }
      } catch (error) {
        console.error('Error loading reference data:', error)
        // Fallback to hardcoded types
        setRegionTypes(getFallbackRegionTypes())
      }
    }
    
    const getFallbackRegionTypes = () => {
      return [
        { id: '46f0ed8d-57e7-4906-9ba9-97425f91dc97', name: 'État', code: 'state' },
        { id: '0d0200a1-6b1d-4dab-b713-b4585d485b6a', name: 'Province', code: 'province' },
        { id: 'b4e613f5-189d-4455-8daa-603e61c76ee6', name: 'Région', code: 'region' },
        { id: '6cef128a-d3b6-4e24-8ff4-2d154ef7cce3', name: 'Territoire', code: 'territory' }
      ]
    }
    
    loadReferenceData()
  }, [])

  const loadOrganization = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const organization = await merchantService.getOrganizationById(id!)
      setOrganization(organization)
    } catch (err: any) {
      console.error('Error loading organization:', err)
      setError(err.response?.data?.error || 'Erreur lors du chargement de l\'organisation')
    } finally {
      setLoading(false)
    }
  }

  const loadOrganizationRegions = async () => {
    try {
      setLoadingRegions(true)
      const response = await merchantService.getOrganizationRegions(id!)
      setRegions(response.regions || [])
    } catch (err: any) {
      console.error('Error loading organization regions:', err)
      // Don't show error for regions, just log it
    } finally {
      setLoadingRegions(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette organisation ? Cette action est irréversible.')) {
      return
    }
    
    try {
      await merchantService.deleteOrganization(id!)
      alert('Organisation supprimée avec succès')
      navigate('/organizations')
    } catch (err: any) {
      console.error('Error deleting organization:', err)
      alert(err.response?.data?.error || 'Erreur lors de la suppression')
    }
  }

  const handleToggleStatus = async () => {
    if (!organization) return
    
    try {
      const updateData: UpdateOrganizationDto = {
        is_active: !organization.is_active
      }
      await merchantService.updateOrganization(id!, updateData)
      alert(`Organisation ${!organization.is_active ? 'activée' : 'désactivée'} avec succès`)
      loadOrganization() // Reload after update
    } catch (err: any) {
      console.error('Error toggling organization status:', err)
      alert(err.response?.data?.error || 'Erreur lors de la modification du statut')
    }
  }

  const handleAddRegion = async () => {
    if (!id) return
    
    try {
      setAddingRegion(true)
      
      if (addRegionMode === 'select') {
        if (!selectedGlobalRegionId || selectedGlobalRegionId === 'undefined' || selectedGlobalRegionId === 'null') {
          alert('Veuillez sélectionner une région existante')
          return
        }
        // Link existing global region to organization
        await merchantService.linkGlobalRegionToOrganization(id, selectedGlobalRegionId, {
          description: newRegionData.description,
          is_active: newRegionData.is_active
        })
        alert('Région globale associée avec succès')
      } else if (addRegionMode === 'create') {
        // Create new global region first, then link it
        if (!newGlobalRegionData.name || !newGlobalRegionData.code || !newGlobalRegionData.country_id) {
          alert('Veuillez remplir le nom, le code et le pays de la région')
          return
        }
        
        // Create global region
        const globalRegion = await merchantService.createGlobalRegion(newGlobalRegionData)
        if (!globalRegion) {
          throw new Error('Erreur lors de la création de la région globale')
        }
        
        // Link it to organization
        await merchantService.linkGlobalRegionToOrganization(id, globalRegion.id, {
          description: newRegionData.description,
          is_active: newRegionData.is_active
        })
        alert('Nouvelle région globale créée et associée avec succès')
      } else {
        // Fallback: create organization region without global link
        if (!newRegionData.name || !newRegionData.code) {
          alert('Veuillez remplir le nom et le code de la région')
          return
        }
        
        await merchantService.createOrganizationRegion(id, newRegionData)
        alert('Région ajoutée avec succès')
      }
      
      // Reset and close modal
      setShowAddRegionModal(false)
      resetModalState()
      loadOrganizationRegions() // Reload regions list
    } catch (err: any) {
      console.error('Error adding region:', err)
      alert(err.response?.data?.error || err.message || 'Erreur lors de l\'ajout de la région')
    } finally {
      setAddingRegion(false)
    }
  }

  const resetModalState = () => {
    setAddRegionMode('select')
    setSelectedGlobalRegionId('')
    setNewRegionData({
      name: '',
      code: '',
      description: '',
      is_active: true
    })
    setNewGlobalRegionData({
      name: '',
      code: '',
      country_id: '',
      type_id: '',
      tax_rate: 0,
      enabled: true
    })
  }

  const handleGlobalRegionSelect = (regionId: string) => {
    setSelectedGlobalRegionId(regionId)
  }

  const handleSwitchToCreateMode = () => {
    setAddRegionMode('create')
    // Pre-fill with search term if available
    if (newRegionData.name) {
      setNewGlobalRegionData((prev: any) => ({
        ...prev,
        name: newRegionData.name,
        code: newRegionData.code || newRegionData.name.toUpperCase().substring(0, 10)
      }))
    }
  }

  const handleSwitchToSelectMode = () => {
    setAddRegionMode('select')
  }

  const handleDeleteRegion = async (regionId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette région ?')) {
      return
    }
    
    try {
      await merchantService.deleteRegion(regionId)
      alert('Région supprimée avec succès')
      loadOrganizationRegions() // Reload regions list
    } catch (err: any) {
      console.error('Error deleting region:', err)
      alert(err.response?.data?.error || 'Erreur lors de la suppression de la région')
    }
  }

  const handleToggleRegionStatus = async (region: OrganizationRegion) => {
    try {
      const updateData = {
        is_active: !region.is_active
      }
      await merchantService.updateRegion(region.region_id, updateData)
      alert(`Région ${!region.is_active ? 'activée' : 'désactivée'} avec succès`)
      loadOrganizationRegions() // Reload regions list
    } catch (err: any) {
      console.error('Error toggling region status:', err)
      alert(err.response?.data?.error || 'Erreur lors de la modification du statut')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error || !organization) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <Link
            to="/organizations"
            className="mr-4 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Détails de l'organisation</h1>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erreur</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error || 'Organisation non trouvée'}</p>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Link
              to="/organizations"
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Retour à la liste
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Link
            to="/organizations"
            className="mr-4 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{organization.name}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Détails de l'organisation (marque globale)
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleToggleStatus}
            className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium ${
              organization.is_active
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                : 'bg-green-100 text-green-800 hover:bg-green-200'
            }`}
          >
            {organization.is_active ? (
              <>
                <XCircle className="h-4 w-4 mr-2" />
                Désactiver
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Activer
              </>
            )}
          </button>
          
          <Link
            to={`/organizations/${organization.organization_id}/edit`}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Edit className="h-4 w-4 mr-2" />
            Modifier
          </Link>
          
          <button
            onClick={handleDelete}
            className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Supprimer
          </button>
        </div>
      </div>

      {/* Organization Info Card */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-16 w-16">
              <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                <Globe className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <div className="ml-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {organization.name}
              </h3>
              <div className="mt-1">
                {organization.is_active ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Actif
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="h-3 w-3 mr-1" />
                    Inactif
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            {/* Description */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <Globe className="h-4 w-4 mr-2" />
                Description
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {organization.description || 'Aucune description fournie'}
              </dd>
            </div>
            
            {/* Organization ID */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                ID de l'organisation
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">
                {organization.organization_id}
              </dd>
            </div>
            
            {/* Created Date */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                Date de création
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(organization.created_at)}
              </dd>
            </div>
            
            {/* Last Updated */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Dernière mise à jour
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(organization.updated_at)}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Regions Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
            <MapPin className="h-5 w-5 mr-2" />
            Régions associées
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Régions géographiques où cette organisation est active
          </p>
        </div>
        
        <div className="px-4 py-5 sm:p-6">
          {loadingRegions ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : regions.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">Aucune région associée</h3>
              <p className="text-gray-500">
                Cette organisation n'a pas encore de régions géographiques définies.
              </p>
              <div className="mt-4">
                <button
                  onClick={() => setShowAddRegionModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une région
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {regions.map((region) => (
                <div key={region.region_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{region.name}</h4>
                      <p className="text-sm text-gray-500">Code: {region.code}</p>
                    </div>
                    {region.is_active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        Inactif
                      </span>
                    )}
                  </div>
                  {region.description && (
                    <p className="mt-2 text-sm text-gray-500">{region.description}</p>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-xs text-gray-400">
                      Créé le {formatDate(region.created_at)}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleToggleRegionStatus(region)}
                        className={`text-xs px-2 py-1 rounded ${region.is_active ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}
                      >
                        {region.is_active ? 'Désactiver' : 'Activer'}
                      </button>
                      <button
                        onClick={() => handleDeleteRegion(region.region_id)}
                        className="text-xs px-2 py-1 rounded bg-red-100 text-red-800 hover:bg-red-200"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Region Modal - New Dual Mode Interface */}
      {showAddRegionModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Ajouter une région</h3>
                <button
                  onClick={() => setShowAddRegionModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Sélectionnez une région existante ou créez-en une nouvelle pour éviter les doublons
              </p>
            </div>
            
            <div className="px-6 py-4">
              {/* Mode Selection Tabs */}
              <div className="mb-6">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={handleSwitchToSelectMode}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${addRegionMode === 'select' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                      <Search className="h-4 w-4 inline mr-2" />
                      Sélectionner une région existante
                    </button>
                    <button
                      onClick={handleSwitchToCreateMode}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${addRegionMode === 'create' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                      <Plus className="h-4 w-4 inline mr-2" />
                      Créer une nouvelle région
                    </button>
                  </nav>
                </div>
              </div>
              
              {addRegionMode === 'select' ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rechercher et sélectionner une région existante
                    </label>
                    <p className="text-sm text-gray-500 mb-4">
                      Sélectionnez une région géographique existante pour éviter les doublons.
                      Si la région que vous cherchez n'existe pas, passez en mode création.
                    </p>
                    
                    <GlobalRegionSelector
                      onSelect={handleGlobalRegionSelect}
                      onSwitchToCreateMode={handleSwitchToCreateMode}
                      organizationId={id!}
                      selectedRegionId={selectedGlobalRegionId}
                      className="mb-4"
                    />
                    
                    {selectedGlobalRegionId && (
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800">
                          ✓ Région sélectionnée. Vous pouvez ajouter des informations supplémentaires ci-dessous.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Informations supplémentaires (optionnel)</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description pour cette organisation
                        </label>
                        <textarea
                          value={newRegionData.description || ''}
                          onChange={(e) => setNewRegionData({...newRegionData, description: e.target.value})}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Description spécifique pour cette organisation..."
                        />
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is_active_select"
                          checked={newRegionData.is_active}
                          onChange={(e) => setNewRegionData({...newRegionData, is_active: e.target.checked})}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="is_active_select" className="ml-2 block text-sm text-gray-700">
                          Région active pour cette organisation
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Créer une nouvelle région géographique
                    </label>
                    <p className="text-sm text-gray-500 mb-4">
                      Créez une nouvelle région qui pourra être réutilisée par d'autres organisations.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nom de la région *
                        </label>
                        <input
                          type="text"
                          value={newGlobalRegionData.name}
                          onChange={(e) => setNewGlobalRegionData({...newGlobalRegionData, name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ex: Île-de-France"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Code de la région *
                        </label>
                        <input
                          type="text"
                          value={newGlobalRegionData.code}
                          onChange={(e) => setNewGlobalRegionData({...newGlobalRegionData, code: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ex: IDF"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pays *
                        </label>
                        <select
                          value={newGlobalRegionData.country_id}
                          onChange={(e) => setNewGlobalRegionData({...newGlobalRegionData, country_id: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Sélectionner un pays</option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                              {country.name} ({country.code})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Type de région
                        </label>
                        <select
                          value={newGlobalRegionData.type_id}
                          onChange={(e) => setNewGlobalRegionData({...newGlobalRegionData, type_id: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Sélectionner un type</option>
                          {regionTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Taux de taxe (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={newGlobalRegionData.tax_rate}
                          onChange={(e) => setNewGlobalRegionData({...newGlobalRegionData, tax_rate: parseFloat(e.target.value) || 0})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ex: 20.0"
                        />
                      </div>
                      
                      <div className="flex items-center justify-center">
                        <div className="mt-6">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="enabled"
                              checked={newGlobalRegionData.enabled}
                              onChange={(e) => setNewGlobalRegionData({...newGlobalRegionData, enabled: e.target.checked})}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="enabled" className="ml-2 block text-sm text-gray-700">
                              Région activée
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Informations pour cette organisation</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description spécifique (optionnel)
                        </label>
                        <textarea
                          value={newRegionData.description || ''}
                          onChange={(e) => setNewRegionData({...newRegionData, description: e.target.value})}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Description spécifique pour cette organisation..."
                        />
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is_active_create"
                          checked={newRegionData.is_active}
                          onChange={(e) => setNewRegionData({...newRegionData, is_active: e.target.checked})}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="is_active_create" className="ml-2 block text-sm text-gray-700">
                          Région active pour cette organisation
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {addRegionMode === 'select' ? (
                  selectedGlobalRegionId ? (
                    <span className="text-green-600">✓ Prêt à associer la région sélectionnée</span>
                  ) : (
                    <span>Sélectionnez une région existante ou passez en mode création</span>
                  )
                ) : (
                  newGlobalRegionData.name && newGlobalRegionData.code && newGlobalRegionData.country_id ? (
                    <span className="text-green-600">✓ Prêt à créer une nouvelle région</span>
                  ) : (
                    <span>Remplissez les champs obligatoires (nom, code, pays)</span>
                  )
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowAddRegionModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleAddRegion}
                  disabled={addingRegion || (
                    addRegionMode === 'select' ? !selectedGlobalRegionId :
                    !newGlobalRegionData.name || !newGlobalRegionData.code || !newGlobalRegionData.country_id
                  )}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingRegion ? 'Ajout en cours...' : (
                    addRegionMode === 'select' ? 'Associer la région' : 'Créer et associer'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <Link
          to="/organizations"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la liste
        </Link>
        
        <Link
          to={`/organizations/${organization.organization_id}/hierarchy`}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <GitBranch className="h-4 w-4 mr-2" />
          Voir la hiérarchie
        </Link>
        
        <Link
          to={`/organizations/${organization.organization_id}/edit`}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <Edit className="h-4 w-4 mr-2" />
          Modifier l'organisation
        </Link>
      </div>
    </div>
  )
}

export default OrganizationDetails